import React, { useState, useMemo } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  useDroppable,
  useDraggable,
} from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import LeadCard from './LeadCard';

const COLUMNS = [
  {
    id: 'new',
    label: 'Nuevo',
    dotClass: 'bg-blue-400',
    headerClass: 'border-t-blue-400',
    countClass: 'bg-blue-100 text-blue-700',
    dropHighlight: 'bg-blue-50 ring-blue-300',
  },
  {
    id: 'in_conversation',
    label: 'En conversación',
    dotClass: 'bg-amber-400',
    headerClass: 'border-t-amber-400',
    countClass: 'bg-amber-100 text-amber-700',
    dropHighlight: 'bg-amber-50 ring-amber-300',
  },
  {
    id: 'interested',
    label: 'Interesado',
    dotClass: 'bg-emerald-400',
    headerClass: 'border-t-emerald-400',
    countClass: 'bg-emerald-100 text-emerald-700',
    dropHighlight: 'bg-emerald-50 ring-emerald-300',
  },
  {
    id: 'no_response',
    label: 'No responde',
    dotClass: 'bg-slate-400',
    headerClass: 'border-t-slate-400',
    countClass: 'bg-slate-100 text-slate-500',
    dropHighlight: 'bg-slate-50 ring-slate-300',
  },
  {
    id: 'closed_won',
    label: 'Cerrado / Ganado',
    dotClass: 'bg-slate-700',
    headerClass: 'border-t-slate-700',
    countClass: 'bg-slate-800 text-white',
    dropHighlight: 'bg-slate-100 ring-slate-400',
  },
];

// Legacy statuses map to the nearest new-status column
const STATUS_COLUMN_MAP = {
  new: 'new',
  in_conversation: 'in_conversation',
  interested: 'interested',
  no_response: 'no_response',
  closed_won: 'closed_won',
  closed_lost: 'closed_won',
  contacted: 'in_conversation',
  qualified: 'interested',
  closed: 'closed_won',
};

const COLUMN_IDS = COLUMNS.map((c) => c.id);

function KanbanColumn({ column, leads, onCardClick, onLeadUpdated, activeId }) {
  const { setNodeRef, isOver } = useDroppable({ id: column.id });

  return (
    <div className="flex flex-col min-w-[260px] w-[260px] shrink-0">
      <div
        className={`flex items-center justify-between px-3 py-2.5 bg-white rounded-t-xl border border-b-0 border-slate-200 border-t-4 ${column.headerClass}`}
      >
        <div className="flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full ${column.dotClass}`} />
          <span className="text-sm font-semibold text-slate-700">{column.label}</span>
        </div>
        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${column.countClass}`}>
          {leads.length}
        </span>
      </div>

      <div
        ref={setNodeRef}
        className={`flex-1 min-h-[200px] rounded-b-xl border border-t-0 border-slate-200 p-2 space-y-2 transition-all duration-150 ${
          isOver ? `ring-2 ${column.dropHighlight}` : 'bg-slate-50'
        }`}
      >
        {leads.map((lead) => (
          <DraggableCard
            key={lead.id}
            lead={lead}
            onClick={() => onCardClick(lead)}
            onLeadUpdated={onLeadUpdated}
            isGhost={activeId === lead.id}
          />
        ))}
        {leads.length === 0 && !isOver && (
          <p className="text-xs text-slate-400 text-center py-4">Sin leads</p>
        )}
      </div>
    </div>
  );
}

function DraggableCard({ lead, onClick, onLeadUpdated, isGhost }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({ id: lead.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    opacity: isGhost ? 0.3 : 1,
  };

  return (
    <LeadCard
      ref={setNodeRef}
      lead={lead}
      onClick={onClick}
      onLeadUpdated={onLeadUpdated}
      isDragging={isDragging}
      style={style}
      {...attributes}
      {...listeners}
    />
  );
}

export default function KanbanBoard({ leads, onStatusChange, onCardClick, onLeadUpdated }) {
  const [activeId, setActiveId] = useState(null);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } })
  );

  const columnLeads = useMemo(
    () =>
      COLUMNS.reduce((acc, col) => {
        acc[col.id] = leads.filter(
          (l) => (STATUS_COLUMN_MAP[l.status] || 'new') === col.id
        );
        return acc;
      }, {}),
    [leads]
  );

  const leadsById = useMemo(
    () => Object.fromEntries(leads.map((l) => [l.id, l])),
    [leads]
  );

  const activeLead = activeId ? leadsById[activeId] : null;

  const handleDragEnd = ({ active, over }) => {
    setActiveId(null);
    if (!over) return;

    const lead = leadsById[active.id];
    if (!lead) return;

    // over.id is always a column id since only columns are droppables
    const targetColumnId = over.id;
    if (!COLUMN_IDS.includes(targetColumnId)) return;

    const currentColumn = STATUS_COLUMN_MAP[lead.status] || lead.status;
    if (currentColumn === targetColumnId) return;

    onStatusChange(lead.id, targetColumnId, lead.status);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={({ active }) => setActiveId(active.id)}
      onDragCancel={() => setActiveId(null)}
      onDragEnd={handleDragEnd}
    >
      <div className="flex gap-4 overflow-x-auto pb-6 pt-1 px-1">
        {COLUMNS.map((col) => (
          <KanbanColumn
            key={col.id}
            column={col}
            leads={columnLeads[col.id] || []}
            onCardClick={onCardClick}
            onLeadUpdated={onLeadUpdated}
            activeId={activeId}
          />
        ))}
      </div>

      <DragOverlay dropAnimation={null}>
        {activeLead ? (
          <div className="w-[260px]">
            <LeadCard lead={activeLead} isDragging />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
